import { FC } from 'react';
import { cn } from 'shared/lib';
import { FilledButton, Loader, OutlinedButton } from 'shared/ui';
import { Replica } from 'entities/tavus/replica';
import ToolBarOnCall from './ToolBarOnCall';
import PictureInPuctureCamera from './PictureInPuctureCamera';
import PreviewToolMenu from './PreviewToolMenu';
import {
  EPersonaIntegrationMode,
  EPersonaViewMode,
  EPreconversationType,
  ITavusPersonaFormik,
  tavusPersonaStore
} from 'entities/tavus/persona';
import { FormikProps } from 'formik';
import FormInput from 'shared/ui/FormInput';
import { IoResizeOutline } from 'react-icons/io5';
import { Link } from 'react-router-dom';
import { ChatRole, MAIN_ROUTE } from 'shared/model';
import { Captions } from 'widgets/captions';
import { bubbleIcons } from 'features/tavus-widget-persona/model';
import PoweredBy from './PoweredBy';

interface IProps {
  currentReplica?: Replica | null;
  formik: FormikProps<ITavusPersonaFormik>;
}

export const Preview: FC<IProps> = ({ currentReplica, formik }) => {
  const {
    personaViewMode,
    personaIntegrationViewMode,
    isPersonaCreating,
    containPreview
  } = tavusPersonaStore();

  const isBeforeCall = personaViewMode === EPersonaViewMode.BEFORE_CALL;
  const isOnCall = personaViewMode === EPersonaViewMode.ON_CALL;
  const isPostCall = personaViewMode === EPersonaViewMode.POST_CALL;
  const isEmbed = personaIntegrationViewMode === EPersonaIntegrationMode.EMBED;
  const widgetIcons: any = bubbleIcons;

  const isDisabled = !formik.dirty || formik.isSubmitting;

  const windowFields = isEmbed ? formik.values.embed : formik.values.widget;

  return (
    <div className="ag-flex ag-flex-col ag-items-center ag-justify-start ag-w-full ag-space-y-4 ag-mx-auto ag-px-6 ag-mt-10 lg:ag-mt-[106px] ag-relative">
      <PreviewToolMenu />

      <div
        className={`ag-opacity-0 ${
          personaIntegrationViewMode === EPersonaIntegrationMode.WIDGET &&
          'ag-opacity-100'
        } ag-absolute ag-duration-200 ${
          formik.values.widget.bubble_icon_side === 'left'
            ? 'ag-bottom-10 lg:ag-bottom-0 ag-left-0 sm:ag-left-5 xl:ag-left-32'
            : 'ag-bottom-10 lg:ag-bottom-0 ag-right-0 sm:ag-right-5 xl:ag-right-32'
        } `}
      >
        {widgetIcons[
          formik.values.widget.bubble_icon_custom
            ? 'first'
            : formik.values.widget.bubble_icon_variant
        ]?.draw(
          formik.values.widget.bubble_background_color,
          !formik.values.widget.bubble_icon_custom &&
            formik.values.widget.bubble_icon_color
        )}
        {formik.values.widget.bubble_icon_variant === 'custom' && (
          <div className="ag-flex ag-flex-col ag-justify-center ag-items-center ag-absolute ag-right-0 ag-left-0 ag-top-0 ag-bottom-0">
            <img
              src={formik.values.widget.bubble_icon_url}
              className="ag-rounded-full ag-h-6 ag-w-6"
            />
          </div>
        )}
      </div>
      <div className="ag-max-w-full ag-overflow-y-auto ag-h-full ag-rounded-xl ag-duration-200 ag-transition-all">
        <div className="ag-border ag-border-neutral-200 ag-rounded-2xl ag-bg-white">
          <div
            className={cn(
              'ag-h-[664px] ag-w-full ag-relative ag-overflow-hidden',
              !isEmbed && 'ag-aspect-square ag-max-w-[449px]'
            )}
          >
            {currentReplica ? (
              <video
                autoPlay
                muted
                loop
                src={currentReplica.thumbnail_video_url}
                className={cn(
                  `ag-w-full ag-h-[664px] ${
                    containPreview
                      ? 'ag-object-contain'
                      : 'ag-object-cover ag-object-top'
                  } ag-duration-200 ag-transition-all ag-rounded-xl`,
                  isEmbed
                    ? 'ag-aspect-[16/9] lg:ag-aspect-[4/6] xl:ag-aspect-[16/9]'
                    : 'ag-aspect-[4/6]'
                )}
              />
            ) : (
              <div className="ag-w-full ag-h-[343px] ag-rounded-lg ag-bg-neutral-100" />
            )}

            {/* --- ON CALL OVERLAY --- */}
            {isOnCall && currentReplica && (
              <>
                <div className="ag-absolute ag-bottom-20 ag-right-0 ag-left-0 ag-mx-4">
                  <Captions
                    withCaptions={windowFields.captions_by_default}
                    messages={[
                      {
                        content: `Hi, I am ${windowFields.video_agent_name}. How can I help you?`,
                        role: ChatRole.ASSISTANT,
                        timestamp: new Date()
                      }
                    ]}
                    isChatScreen
                  />
                </div>
                <ToolBarOnCall windowFields={windowFields} />
                <PictureInPuctureCamera windowFields={windowFields} />
                <button
                  type="button"
                  className="ag-absolute ag-top-3 ag-right-3 ag-rounded-[10px] ag-bg-white-opacity-70 ag-p-2 ag-h-10 ag-w-10 ag-flex ag-flex-col ag-justify-center ag-text-dark-purple"
                >
                  <IoResizeOutline size={24} color="currentColor" />
                </button>
              </>
            )}

            {/* --- POST CALL OVERLAY --- */}
            {isPostCall && (
              <div
                className="ag-absolute ag-inset-0 ag-backdrop-blur-2xl ag-rounded-2xl ag-bg-black/60 ag-flex ag-flex-col ag-items-center ag-justify-center ag-text-center ag-gap-y-5"
                style={{ fontFamily: `${windowFields.font}` }}
              >
                <h2 className="ag-text-white ag-text-3xl ag-font-bold">
                  Video call ended
                </h2>
                <p className="ag-text-white ag-text-lg">
                  {windowFields.video_call_ended_custom_message}
                </p>
                <div
                  className={`ag-flex ag-mx-auto ag-duration-200 ag-transition-all ${
                    isEmbed
                      ? 'ag-flex-col sm:ag-flex-row'
                      : 'ag-flex-col ag-w-[70%]'
                  } ag-gap-4`}
                >
                  {windowFields?.cta_primary_button_link ? (
                    <a
                      href={windowFields?.cta_primary_button_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ag-bg-purple-600 hover:ag-bg-purple-700 ag-text-white ag-px-6 ag-py-2 ag-rounded-md ag-text-sm"
                    >
                      {windowFields?.cta_primary_button_text}
                    </a>
                  ) : (
                    <FilledButton className="ag-bg-purple-600 hover:ag-bg-purple-700 ag-text-white ag-px-6 ag-py-2">
                      {windowFields?.cta_primary_button_text}
                    </FilledButton>
                  )}
                  {windowFields?.cta_secondary_button_text &&
                    windowFields?.cta_secondary_button_link && (
                      <a
                        href={windowFields?.cta_secondary_button_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ag-border ag-rounded-md ag-duration-200 ag-border-white ag-text-white hover:ag-bg-white ag-text-sm hover:ag-text-black ag-px-6 ag-py-2"
                      >
                        {windowFields?.cta_secondary_button_text}
                      </a>
                    )}
                </div>
              </div>
            )}

            {/* --- EMBED WIDGET (BEFORE CALL) OVERLAY --- */}
            {isBeforeCall && currentReplica && (
              <div
                style={{ fontFamily: `${windowFields.font}` }}
                className="ag-absolute ag-inset-0 ag-backdrop-blur-2xl ag-rounded-2xl ag-overflow-hidden"
              >
                <div className="ag-flex ag-flex-col ag-justify-center ag-items-center ag-text-center ag-h-full ag-gap-y-5 ag-relative ag-mx-auto ag-w-[90%] sm:ag-max-w-[338px]">
                  <div className="ag-flex ag-flex-col ag-gap-y-5 ag-justify-center ag-items-center ag-h-full">
                    <ul className="ag-text-white ag-flex ag-flex-col ag-gap-y-4">
                      <li className="ag-text-2xl ag-font-semibold">
                        {windowFields.video_agent_name}
                      </li>
                      <li className="ag-text-xl">{windowFields.description}</li>
                    </ul>
                    {windowFields.is_form_enabled && (
                      <div className="ag-space-y-4 ag-w-full">
                        {formik.values.preconversationConfig.fields.map(
                          (field, index) => (
                            <FormInput
                              key={field.name}
                              formik={formik}
                              disabled
                              id={field.name}
                              name={`preconversationConfig.fields[${index}].value`}
                              type={
                                field.type === EPreconversationType.TEXT
                                  ? 'text'
                                  : 'email'
                              }
                              className="ag-border !ag-bg-[#ffffff3a] placeholder:ag-text-gray-400 ag-w-full ag-border-gray-300 ag-rounded-md ag-px-3 ag-py-2 ag-text-sm"
                              placeholder={field.label}
                            />
                          )
                        )}
                      </div>
                    )}
                    <FilledButton
                      style={{
                        backgroundColor: isEmbed
                          ? windowFields.main_color
                          : windowFields.more_personalised_colors
                          ? formik.values.widget.welcome_button_color
                          : windowFields.main_color
                      }}
                      className="ag-w-full ag-px-4 ag-py-2"
                    >
                      {windowFields.button_text}
                    </FilledButton>
                  </div>
                  <div className="flex flex-col ag-justify-end ag-items-end">
                    <PoweredBy isPoweredBy={windowFields?.powered_by} />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* --- Bottom Mobile Buttons --- */}
        <div className="ag-p-4 ag-flex ag-gap-4 lg:ag-hidden ag-bottom-0 ag-left-0 ag-right-0 ag-h-[72px] ag-z-50">
          <Link to={MAIN_ROUTE} className="ag-w-full">
            <OutlinedButton className="ag-h-full ag-w-full ag-bg-white ag-text-neutral-600">
              Cancel
            </OutlinedButton>
          </Link>
          <FilledButton
            className="ag-h-full ag-w-full disabled:ag-bg-primary-600 disabled:ag-ag-opacity-20"
            disabled={isDisabled}
            type="submit"
          >
            {isPersonaCreating ? <Loader isButton /> : 'Save changes'}
          </FilledButton>
        </div>
      </div>
    </div>
  );
};
